import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { useAudioExportSettings } from "@/lib/audioExportFormats";
import { nativeAudioDownload, requestAudioExport } from "@/lib/stemAudioExport";
import AudioExportControls from "@/components/audio-tools/AudioExportControls";
import StemDownloads from "@/components/audio-tools/StemDownloads";
import useStemTask from "@/components/stems/useStemTask";
import { stemsAreReady, stemStatusLabel } from "@/components/stems/stemTask";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Crown,
  Download,
  Drum,
  Guitar,
  Loader2,
  Lock,
  Mic2,
  Pause,
  Play,
  Repeat,
  RotateCcw,
  Save,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Waves,
  AudioWaveform,
  Sparkles,
  Trash2,
} from "lucide-react";
import { trackProductEvent } from "@/lib/productMetrics";
import ChordLearningPanel from "@/components/music/ChordLearningPanel";
import { FLAT_TO_SHARP, NOTE_ORDER, getChordNotes } from "@/music/chords/chordParser";
import {
  readLearningInstrumentPreference,
  writeLearningInstrumentPreference,
} from "@/music/chords/learningInstrumentPreference";
import {
  buildChordSyncState,
  buildTimelineStarts,
  findActiveTimelineIndexAtTime,
  normalizeChordTimeline,
  normalizeSectionTimeline,
  toFiniteNumber,
} from "@/music/chords/chordSyncUtils";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_STEMS = ["vocals", "drums", "bass", "other"];

const STEM_META = {
  vocals: { label: "Vocals", Icon: Mic2, color: "text-pink-300" },
  drums: { label: "Drums", Icon: Drum, color: "text-orange-300" },
  bass: { label: "Bass", Icon: AudioWaveform, color: "text-tune" },
  other: { label: "Other", Icon: Guitar, color: "text-sky-300" },
  guitar: { label: "Guitar", Icon: Guitar, color: "text-brass" },
  piano: { label: "Piano / Keys", Icon: Sparkles, color: "text-signal" },
  keys: { label: "Keys", Icon: Sparkles, color: "text-signal" },
};

const DEFAULT_STEM_SETTINGS = {
  volume: 1,
  muted: false,
  solo: false,
  pan: 0,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const getStemAnalysisInstrument = (stemKey, learningInstrument) => {
  if (stemKey === "bass") return "bass";
  if (stemKey === "drums" || stemKey === "vocals") return stemKey;
  if (stemKey === "piano" || stemKey === "keys") return "piano";
  if (stemKey === "guitar") return "guitar";
  return learningInstrument || "guitar";
};

const normalizeStemChordTimelineForFrontend = (timeline = []) =>
  (Array.isArray(timeline) ? timeline : []).map((item) => {
    const start = toFiniteNumber(item.startTime, toFiniteNumber(item.start_time, 0)) || 0;
    const end = toFiniteNumber(item.endTime, toFiniteNumber(item.end_time, start + 1)) || start + 1;
    return {
      ...item,
      chord: item.chordName || item.chord_name || item.chord || item.root || "No chord",
      time: start,
      start,
      end,
      duration: Math.max(0.25, end - start),
      confidence: toFiniteNumber(item.confidence, 0.5),
      bass_note: item.root || item.bass_note,
      chord_quality: item.quality || item.chord_quality,
      detectedNotes: item.detectedNotes || item.detected_notes || [],
      instrumentType: item.instrumentType || item.instrument_type,
      sourceStem: item.sourceStem || item.source_stem,
      inferredChord: item.inferredChord ?? item.inferred_chord,
    };
  });

const slugifyFilename = (filename) =>
  (filename || "song")
    .replace(/\.[^.]+$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "song";

const normalizeRoot = (root) => FLAT_TO_SHARP[root] || root;

const transposeNote = (root, steps) => {
  if (!root || !Number.isInteger(steps) || steps === 0) return root;
  const normalized = normalizeRoot(root);
  const index = NOTE_ORDER.indexOf(normalized);
  if (index < 0) return root;
  const nextIndex = (index + steps + NOTE_ORDER.length) % NOTE_ORDER.length;
  return NOTE_ORDER[nextIndex];
};

const transposeChord = (chord, steps) => {
  if (!chord || steps === 0) return chord;

  const [mainPart, bassPart] = chord.split("/");
  const mainMatch = mainPart.match(/^([A-G](?:#|b)?)(.*)$/);
  if (!mainMatch) return chord;

  const [, root, suffix] = mainMatch;
  const newRoot = transposeNote(root, steps);

  let newBassPart = bassPart;
  if (bassPart) {
    const bassMatch = bassPart.match(/^([A-G](?:#|b)?)(.*)$/);
    if (bassMatch) {
      const [, bassRoot, bassSuffix] = bassMatch;
      newBassPart = `${transposeNote(bassRoot, steps)}${bassSuffix || ""}`;
    }
  }

  return newBassPart ? `${newRoot}${suffix || ""}/${newBassPart}` : `${newRoot}${suffix || ""}`;
};

const ensureStemSettings = (keys, rawSettings) => {
  const next = {};
  keys.forEach((key) => {
    const incoming = rawSettings?.[key] || {};
    next[key] = {
      volume: clamp(Number(incoming.volume ?? DEFAULT_STEM_SETTINGS.volume), 0, 1),
      muted: Boolean(incoming.muted),
      solo: Boolean(incoming.solo),
      pan: clamp(Number(incoming.pan ?? DEFAULT_STEM_SETTINGS.pan), -1, 1),
    };
  });
  return next;
};

const UI_SYNC_INTERVAL_MS = 100;
const TIME_EPSILON = 0.02;
const DRIFT_SYNC_INTERVAL_MS = 500;
const DRIFT_EPSILON_SECONDS = 0.08;
const CHORD_LEARNING_SYNC_INTERVAL_MS = 150;
const EMPTY_CHORD_SYNC = {
  activeIndex: -1,
  activeChord: null,
  nextIndex: -1,
  nextChord: null,
  timeToNextChord: null,
};

const StemMixerPage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const isPro = (user?.plan || "free") === "pro";

  const baseAudioRef = useRef(null);
  const stemAudioRefs = useRef({});
  const metronomeContextRef = useRef(null);
  const sequenceCursorRef = useRef(0);
  const sequenceRepeatRef = useRef(1);
  const playbackTimeRef = useRef(0);
  const chordIndexRef = useRef(-1);
  const sectionIndexRef = useRef(-1);
  const lastUiSyncRef = useRef(0);
  const lastDriftSyncRef = useRef(0);
  const lastChordLearningSyncRef = useRef(0);
  const progressBarRef = useRef(null);
  const timelineDraggingRef = useRef(false);
  const pianoLearningTrackedRef = useRef(false);

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stemSearchParams] = useSearchParams();
  const { task: stemsTask, retry: prepareStems, retrying: preparingStems, error: stemsStatusError } = useStemTask(analysisId, getAuthHeaders, stemSearchParams.get("task") || "");
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [updatingPreset, setUpdatingPreset] = useState(false);
  const [deletingPreset, setDeletingPreset] = useState(false);
  const exportSettings = useAudioExportSettings("stems");
  const exportFormat = exportSettings.format;
  const mixExportController = useRef(null);
  useEffect(() => () => mixExportController.current?.abort(), []);
  const [exportingMix, setExportingMix] = useState(false);
  const [exportUseSequence, setExportUseSequence] = useState(false);
  const [exportQuality, setExportQuality] = useState("balanced");
  const [exportSubtractionStrength, setExportSubtractionStrength] = useState(1.0);
  const [exportEnhanceStems, setExportEnhanceStems] = useState(true);
  const [showAdvancedExport, setShowAdvancedExport] = useState(false);
  const [exportStatusMsg, setExportStatusMsg] = useState("");

  const [isPlaying, setIsPlaying] = useState(false);
  const [masterVolume, setMasterVolume] = useState(90);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopSectionIndex, setLoopSectionIndex] = useState(null);
  const [sequenceModeEnabled, setSequenceModeEnabled] = useState(false);
  const [sequenceCursor, setSequenceCursor] = useState(0);
  const [sequenceRepeatProgress, setSequenceRepeatProgress] = useState(1);
  const [clickEnabled, setClickEnabled] = useState(false);
  const [clickSubdivision, setClickSubdivision] = useState(1);
  const [transposeSteps, setTransposeSteps] = useState(0);
  const [activeStemKey, setActiveStemKey] = useState("vocals");
  const [stemSettings, setStemSettings] = useState(
    ensureStemSettings(BASE_STEMS, {})
  );
  const [sequence, setSequence] = useState([]);
  const [currentSectionName, setCurrentSectionName] = useState("Sin sección");
  const [chordSync, setChordSync] = useState(EMPTY_CHORD_SYNC);
  const [learningInstrument, setLearningInstrument] = useState(() => readLearningInstrumentPreference());
  const [chordSourceMode, setChordSourceMode] = useState("global");
  const [stemChordAnalyses, setStemChordAnalyses] = useState({});
  const [stemChordLoadingKey, setStemChordLoadingKey] = useState(null);
  const [stemChordError, setStemChordError] = useState("");

  const analysisDuration = duration || toFiniteNumber(analysis?.duration, 0) || 0;
  const globalChords = useMemo(
    () => normalizeChordTimeline(analysis?.chords || [], analysisDuration),
    [analysis?.chords, analysisDuration]
  );
  const stemAnalysisInstrument = getStemAnalysisInstrument(activeStemKey, learningInstrument);
  const stemChordCacheKey = stemsTask?.id
    ? `${stemsTask.id}:${activeStemKey}:${stemAnalysisInstrument}`
    : "";
  const activeStemChordAnalysis = stemChordCacheKey ? stemChordAnalyses[stemChordCacheKey] : null;
  const stemChords = useMemo(
    () =>
      normalizeChordTimeline(
        normalizeStemChordTimelineForFrontend(activeStemChordAnalysis?.timeline || []),
        analysisDuration
      ),
    [activeStemChordAnalysis, analysisDuration]
  );
  const usingStemChordTimeline = chordSourceMode === "stem" && stemChords.length > 0;
  const chords = usingStemChordTimeline ? stemChords : globalChords;
  const sections = useMemo(
    () => normalizeSectionTimeline(analysis?.structure || [], analysisDuration),
    [analysis?.structure, analysisDuration]
  );
  const chordStarts = useMemo(() => buildTimelineStarts(chords), [chords]);
  const sectionStarts = useMemo(() => buildTimelineStarts(sections), [sections]);
  const selectedLoopSection = useMemo(
    () => (loopSectionIndex !== null ? sections[loopSectionIndex] ?? null : null),
    [loopSectionIndex, sections]
  );
  const playbackAudioUrl = analysis?.playback_url || analysis?.preview_audio_url || analysis?.audio_url || "";

  const trackEvent = useCallback(
    (event, payload = {}) => {
      console.log(
        "[StemMixerTracking]",
        JSON.stringify({
          event,
          analysis_id: analysisId,
          plan: isPro ? "pro" : "free",
          timestamp: new Date().toISOString(),
          ...payload,
        })
      );
      if (event === "save_preset_free_attempt" || event === "preset_save_403") {
        trackProductEvent(
          "preset_save_attempt",
          { analysis_id: analysisId, source_event: event, ...payload },
          getAuthHeaders
        );
      }
    },
    [analysisId, getAuthHeaders, isPro]
  );

  useEffect(() => {
    trackEvent("stem_mixer_page_opened");
  }, [trackEvent]);

  useEffect(() => {
    if (!analysis || !chords.length || pianoLearningTrackedRef.current) return;
    pianoLearningTrackedRef.current = true;
    trackEvent("piano_learning_mode_used", { chord_count: chords.length, instrument: learningInstrument });
  }, [analysis, chords.length, learningInstrument, trackEvent]);

  const handleLearningInstrumentChange = useCallback((instrument) => {
    setLearningInstrument(writeLearningInstrumentPreference(instrument));
  }, []);

  const stemsReady = useMemo(() => {
    return stemsAreReady(stemsTask);
  }, [stemsTask]);

  const availableStemKeys = useMemo(() => {
    if (!stemsReady) return BASE_STEMS;
    const keys = Object.keys(stemsTask.stems || {}).filter((key) => Boolean(stemsTask.stems[key]));
    const ordered = [...BASE_STEMS.filter((key) => keys.includes(key)), ...keys.filter((key) => !BASE_STEMS.includes(key))];
    return ordered.length > 0 ? ordered : BASE_STEMS;
  }, [stemsReady, stemsTask]);

  useEffect(() => {
    setStemSettings((prev) => ensureStemSettings(availableStemKeys, prev));
  }, [availableStemKeys]);

  const getPlaybackAudios = useCallback(() => {
    if (stemsReady) {
      return availableStemKeys
        .map((key) => stemAudioRefs.current[key])
        .filter(Boolean);
    }
    return baseAudioRef.current ? [baseAudioRef.current] : [];
  }, [availableStemKeys, stemsReady]);

  const getLeaderAudio = useCallback(() => {
    const audios = getPlaybackAudios();
    return audios.length ? audios[0] : null;
  }, [getPlaybackAudios]);

  const pauseAllPlayback = useCallback(() => {
    getPlaybackAudios().forEach((audio) => audio.pause());
    setIsPlaying(false);
  }, [getPlaybackAudios]);

  const syncTimelineAtTime = useCallback(
    (time, { forceUi = false } = {}) => {
      const safeTime = clamp(Number(time) || 0, 0, analysisDuration || 0);
      playbackTimeRef.current = safeTime;

      const nextSync = buildChordSyncState(chords, chordStarts, safeTime, analysisDuration, TIME_EPSILON);
      const { activeIndex: chordIndex, activeChord, nextIndex, nextChord, timeToNextChord } = nextSync;

      const chordChanged = chordIndex !== chordIndexRef.current;
      const now = performance.now();
      const shouldUpdateChordUi =
        forceUi ||
        chordChanged ||
        now - lastChordLearningSyncRef.current >= CHORD_LEARNING_SYNC_INTERVAL_MS;

      chordIndexRef.current = chordIndex;

      if (shouldUpdateChordUi) {
        lastChordLearningSyncRef.current = now;
        setChordSync((prev) => {
          if (
            prev.activeIndex === chordIndex &&
            prev.nextIndex === nextIndex &&
            prev.activeChord === activeChord &&
            prev.nextChord === nextChord &&
            prev.timeToNextChord === timeToNextChord
          ) {
            return prev;
          }

          return {
            activeIndex: chordIndex,
            activeChord,
            nextIndex,
            nextChord,
            timeToNextChord,
          };
        });
      }

      const sectionIndex = findActiveTimelineIndexAtTime(sections, sectionStarts, safeTime, 0);
      if (sectionIndex !== sectionIndexRef.current) {
        sectionIndexRef.current = sectionIndex;
        setCurrentSectionName(sectionIndex >= 0 ? sections[sectionIndex].name : "Sin sección");
      }

      if (forceUi) {
        lastUiSyncRef.current = performance.now();
        setCurrentTime((prev) => (Math.abs(prev - safeTime) > TIME_EPSILON ? safeTime : prev));
      }
    },
    [analysisDuration, chordStarts, chords, sectionStarts, sections]
  );

  const seekTo = useCallback(
    (time) => {
      const safeTime = clamp(Number(time) || 0, 0, duration || analysisDuration || 0);
      getPlaybackAudios().forEach((audio) => {
        audio.currentTime = safeTime;
      });
      syncTimelineAtTime(safeTime, { forceUi: true });
    },
    [analysisDuration, duration, getPlaybackAudios, syncTimelineAtTime]
  );

  const syncFollowerAudioDrift = useCallback(
    (leader, leaderTime) => {
      if (!stemsReady) return;
      const now = performance.now();
      if (now - lastDriftSyncRef.current < DRIFT_SYNC_INTERVAL_MS) return;
      lastDriftSyncRef.current = now;

      getPlaybackAudios().forEach((audio) => {
        if (!audio || audio === leader || audio.paused) return;
        if (Math.abs((audio.currentTime || 0) - leaderTime) > DRIFT_EPSILON_SECONDS) {
          audio.currentTime = leaderTime;
        }
      });
    },
    [getPlaybackAudios, stemsReady]
  );

  const showProCTA = useCallback((message) => {
    toast.info(`🔒 Solo en PRO — ${message}`, {
      description: "Upgrade a PRO para desbloquear herramientas de ensayo avanzadas.",
    });
  }, []);

  const hydrateAnalysis = useCallback((data) => {
    setAnalysis(data);
    setDuration(data.duration || 0);
    playbackTimeRef.current = 0;
    chordIndexRef.current = -1;
    sectionIndexRef.current = -1;
    lastUiSyncRef.current = 0;
    lastChordLearningSyncRef.current = 0;
    pianoLearningTrackedRef.current = false;
    setCurrentTime(0);
    setChordSync(EMPTY_CHORD_SYNC);
    setCurrentSectionName("Sin sección");
    setPresetName((prev) => prev || `${slugifyFilename(data.filename)}_rehearsal`);
    setSequence(
      (data.structure || []).map((section) => ({
        section_name: section.name,
        start: Number(section.start || 0),
        end: Number(section.end || 0),
        repeats: 1,
      }))
    );
  }, []);

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/analysis/${analysisId}`, {
        headers: getAuthHeaders(),
      });
      hydrateAnalysis(response.data);
    } catch (error) {
      toast.error("No fue posible cargar el análisis.");
      navigate("/dashboard");
    }
  }, [analysisId, getAuthHeaders, hydrateAnalysis, navigate]);

  const fetchPresets = useCallback(async () => {
    setPresetsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stem-mixer/presets`, {
        headers: getAuthHeaders(),
        params: { analysis_id: analysisId },
      });
      setPresets(response.data || []);
    } catch (error) {
      toast.error("No fue posible cargar presets.");
    } finally {
      setPresetsLoading(false);
    }
  }, [analysisId, getAuthHeaders]);

  useEffect(() => {
    let mounted = true;
    const bootstrap = async () => {
      setLoading(true);
      await Promise.all([fetchAnalysis(), fetchPresets()]);
      if (mounted) setLoading(false);
    };
    bootstrap();
    return () => {
      mounted = false;
    };
  }, [fetchAnalysis, fetchPresets]);

  useEffect(() => {
    if (chordSourceMode !== "stem" || !stemsReady || !stemsTask?.id || !activeStemKey) {
      return undefined;
    }
    if (stemChordCacheKey && stemChordAnalyses[stemChordCacheKey]) {
      setStemChordError("");
      return undefined;
    }

    let cancelled = false;
    setStemChordLoadingKey(stemChordCacheKey);
    setStemChordError("");

    axios
      .post(
        `${API_URL}/stems/task/${stemsTask.id}/analyze/${activeStemKey}`,
        { instrument_type: stemAnalysisInstrument },
        { headers: getAuthHeaders() }
      )
      .then((response) => {
        if (cancelled) return;
        setStemChordAnalyses((prev) => ({
          ...prev,
          [stemChordCacheKey]: response.data,
        }));
        trackEvent("stem_chord_timeline_loaded", {
          stem: activeStemKey,
          instrument: stemAnalysisInstrument,
          segments: response.data?.timeline?.length || 0,
          cache_hit: Boolean(response.data?.cache_hit),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error?.response?.data?.detail || "No fue posible analizar acordes de este stem.";
        setStemChordError(message);
        console.error("Error loading stem chord analysis", error);
      })
      .finally(() => {
        if (!cancelled) setStemChordLoadingKey(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeStemKey,
    chordSourceMode,
    getAuthHeaders,
    stemAnalysisInstrument,
    stemChordAnalyses,
    stemChordCacheKey,
    stemsReady,
    stemsTask?.id,
    trackEvent,
  ]);

  const hasSoloActive = useMemo(
    () => availableStemKeys.some((key) => stemSettings[key]?.solo),
    [availableStemKeys, stemSettings]
  );

  useEffect(() => {
    const playbackRateValue = isPro ? playbackRate : 1;
    const master = clamp(masterVolume / 100, 0, 1);

    if (stemsReady) {
      availableStemKeys.forEach((key) => {
        const audio = stemAudioRefs.current[key];
        if (!audio) return;
        const settings = stemSettings[key] || DEFAULT_STEM_SETTINGS;
        const blockedBySolo = hasSoloActive && !settings.solo;
        const effective = settings.muted || blockedBySolo ? 0 : settings.volume * master;
        audio.playbackRate = playbackRateValue;
        audio.volume = clamp(effective, 0, 1);
      });
      return;
    }

    if (baseAudioRef.current) {
      baseAudioRef.current.playbackRate = playbackRateValue;
      baseAudioRef.current.volume = master;
    }
  }, [availableStemKeys, hasSoloActive, isPro, masterVolume, playbackRate, stemSettings, stemsReady]);

  useEffect(() => {
    const leader = getLeaderAudio();
    if (!leader || !analysis) return;

    const onTimeUpdate = () => {
      const t = leader.currentTime || 0;
      syncFollowerAudioDrift(leader, t);
      syncTimelineAtTime(t);

      if (sequenceModeEnabled && sequence.length > 0) {
        const cursor = sequenceCursorRef.current;
        const item = sequence[cursor];
        if (item && t >= item.end) {
          if (sequenceRepeatRef.current < item.repeats) {
            sequenceRepeatRef.current += 1;
            setSequenceRepeatProgress(sequenceRepeatRef.current);
            seekTo(item.start);
            return;
          }

          if (cursor < sequence.length - 1) {
            const nextCursor = cursor + 1;
            sequenceCursorRef.current = nextCursor;
            sequenceRepeatRef.current = 1;
            setSequenceCursor(nextCursor);
            setSequenceRepeatProgress(1);
            seekTo(sequence[nextCursor].start);
            return;
          }

          if (loopEnabled) {
            sequenceCursorRef.current = 0;
            sequenceRepeatRef.current = 1;
            setSequenceCursor(0);
            setSequenceRepeatProgress(1);
            seekTo(sequence[0].start);
            return;
          }

          pauseAllPlayback();
          return;
        }
      }

      if (!sequenceModeEnabled && loopEnabled && selectedLoopSection) {
        if (t >= selectedLoopSection.end) {
          seekTo(selectedLoopSection.start);
          return;
        }
      }

      const now = performance.now();
      if (now - lastUiSyncRef.current >= UI_SYNC_INTERVAL_MS) {
        lastUiSyncRef.current = now;
        setCurrentTime((prev) => (Math.abs(prev - t) > TIME_EPSILON ? t : prev));
      }
    };

    const onLoadedMetadata = () => {
      const nextDuration = leader.duration || analysis.duration || 0;
      setDuration((prev) => (Math.abs(prev - nextDuration) > TIME_EPSILON ? nextDuration : prev));
      syncTimelineAtTime(leader.currentTime || playbackTimeRef.current || 0, { forceUi: true });
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      const pausedTime = leader.currentTime || 0;
      syncTimelineAtTime(pausedTime, { forceUi: true });
    };

    leader.addEventListener("timeupdate", onTimeUpdate);
    leader.addEventListener("loadedmetadata", onLoadedMetadata);
    leader.addEventListener("play", onPlay);
    leader.addEventListener("pause", onPause);

    return () => {
      leader.removeEventListener("timeupdate", onTimeUpdate);
      leader.removeEventListener("loadedmetadata", onLoadedMetadata);
      leader.removeEventListener("play", onPlay);
      leader.removeEventListener("pause", onPause);
    };
  }, [
    analysis,
    getLeaderAudio,
    loopEnabled,
    pauseAllPlayback,
    seekTo,
    selectedLoopSection,
    sequence,
    sequenceModeEnabled,
    syncTimelineAtTime,
    syncFollowerAudioDrift,
  ]);

  useEffect(() => {
    if (!analysis) return;
    syncTimelineAtTime(playbackTimeRef.current || 0, { forceUi: true });
  }, [analysis, chords, sections, syncTimelineAtTime]);

  useEffect(() => {
    if (!analysis || !isPlaying) return undefined;
    let frameId = 0;

    const tick = () => {
      const leader = getLeaderAudio();
      if (leader && !leader.paused) {
        const t = leader.currentTime || 0;
        syncTimelineAtTime(t);
        const now = performance.now();
        if (now - lastUiSyncRef.current >= UI_SYNC_INTERVAL_MS) {
          lastUiSyncRef.current = now;
          setCurrentTime((prev) => (Math.abs(prev - t) > TIME_EPSILON ? t : prev));
        }
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [analysis, getLeaderAudio, isPlaying, syncTimelineAtTime]);

  useEffect(() => {
    if (!analysis) return;
    const t = playbackTimeRef.current || 0;
    getPlaybackAudios().forEach((audio) => {
      if (!audio || Math.abs((audio.currentTime || 0) - t) <= TIME_EPSILON) return;
      try {
        audio.currentTime = t;
      } catch {
        // Some browsers reject currentTime before metadata is ready; loadedmetadata will resync.
      }
    });
    syncTimelineAtTime(t, { forceUi: true });
  }, [analysis, availableStemKeys, getPlaybackAudios, stemsReady, syncTimelineAtTime]);

  const emitMetronomeTick = useCallback((accent = false) => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      if (!metronomeContextRef.current) {
        metronomeContextRef.current = new AudioContextClass();
      }
      const context = metronomeContextRef.current;
      if (context.state === "suspended") {
        context.resume();
      }
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "square";
      oscillator.frequency.value = accent ? 1400 : 920;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.08);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.09);
    } catch (error) {
      console.error("Metronome error", error);
    }
  }, []);

  useEffect(() => {
    if (!isPlaying || !clickEnabled || !analysis?.bpm) return;

    const subdivision = clamp(clickSubdivision, 1, 8);
    const rate = isPro ? playbackRate : 1;
    const intervalMs = (60 / (analysis.bpm * subdivision * rate)) * 1000;
    let tick = 0;

    const timer = setInterval(() => {
      const accent = tick % subdivision === 0;
      emitMetronomeTick(accent);
      tick += 1;
    }, intervalMs);

    return () => clearInterval(timer);
  }, [analysis?.bpm, clickEnabled, clickSubdivision, emitMetronomeTick, isPlaying, isPro, playbackRate]);

  useEffect(
    () => () => {
      pauseAllPlayback();
      if (metronomeContextRef.current) {
        metronomeContextRef.current.close().catch(() => null);
      }
    },
    [pauseAllPlayback]
  );

  const togglePlay = async () => {
    const audios = getPlaybackAudios();
    if (!audios.length) return;

    if (isPlaying) {
      pauseAllPlayback();
      return;
    }

    audios.forEach((audio) => {
      audio.currentTime = playbackTimeRef.current;
    });
    syncTimelineAtTime(playbackTimeRef.current, { forceUi: true });

    try {
      await Promise.all(audios.map((audio) => audio.play()));
      setIsPlaying(true);
    } catch (error) {
      toast.error("No fue posible iniciar reproducción.");
      console.error(error);
    }
  };

  const restartPlayback = () => {
    seekTo(0);
    sequenceCursorRef.current = 0;
    sequenceRepeatRef.current = 1;
    setSequenceCursor(0);
    setSequenceRepeatProgress(1);
  };

  const skipBy = useCallback(
    (seconds) => seekTo(playbackTimeRef.current + seconds),
    [seekTo]
  );

  const seekFromTimelinePointer = useCallback(
    (event) => {
      if (!progressBarRef.current || !duration) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      seekTo(ratio * duration);
    },
    [duration, seekTo]
  );

  const handleTimelinePointerDown = useCallback(
    (event) => {
      timelineDraggingRef.current = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      seekFromTimelinePointer(event);
    },
    [seekFromTimelinePointer]
  );

  const handleTimelinePointerMove = useCallback(
    (event) => {
      if (!timelineDraggingRef.current) return;
      seekFromTimelinePointer(event);
    },
    [seekFromTimelinePointer]
  );

  const handleTimelinePointerUp = useCallback(
    (event) => {
      timelineDraggingRef.current = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      seekFromTimelinePointer(event);
    },
    [seekFromTimelinePointer]
  );

  const handleTimelinePointerCancel = useCallback(() => {
    timelineDraggingRef.current = false;
  }, []);

  const handleTimelineKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        skipBy(-5);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        skipBy(5);
      }
      if (event.key === "Home") {
        event.preventDefault();
        seekTo(0);
      }
      if (event.key === "End") {
        event.preventDefault();
        seekTo(duration || 0);
      }
    },
    [duration, seekTo, skipBy]
  );

  const updateStemSetting = (key, patch) => {
    setActiveStemKey(key);
    setStemSettings((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || DEFAULT_STEM_SETTINGS),
        ...patch,
      },
    }));
  };

  const toggleStemMute = (key) => {
    const nextMuted = !Boolean(stemSettings[key]?.muted);
    updateStemSetting(key, { muted: nextMuted });
    trackEvent("stem_mute_toggle", { stem: key, muted: nextMuted });
  };

  const toggleStemSolo = (key) => {
    const nextSolo = !Boolean(stemSettings[key]?.solo);
    updateStemSetting(key, { solo: nextSolo });
    trackEvent("stem_solo_toggle", { stem: key, solo: nextSolo });
  };

  const applyQuickPreset = (preset) => {
    const base = ensureStemSettings(availableStemKeys, stemSettings);
    let next = base;
    let message = "";

    if (preset === "karaoke") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === "vocals",
            solo: false,
          },
        ])
      );
      setActiveStemKey("vocals");
      message = "Preset Karaoke aplicado.";
    }

    if (preset === "bass_drums") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: false,
            solo: key === "bass" || key === "drums",
          },
        ])
      );
      setActiveStemKey("bass");
      message = "Preset Bajo + bateria aplicado.";
    }

    if (preset === "no_bass") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === "bass",
            solo: false,
          },
        ])
      );
      setActiveStemKey("bass");
      message = "Preset Sin bajo aplicado.";
    }

    if (preset === "no_drums") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === "drums",
            solo: false,
          },
        ])
      );
      setActiveStemKey("drums");
      message = "Preset Sin bateria aplicado.";
    }

    if (preset === "no_other") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === "other",
            solo: false,
          },
        ])
      );
      setActiveStemKey("other");
      message = "Preset Sin other aplicado.";
    }

    if (preset === "instrumental_full") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === "vocals",
            solo: false,
          },
        ])
      );
      setActiveStemKey("other");
      message = "Preset Instrumental completo aplicado.";
    }

    if (preset === "vocals_only") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: false,
            solo: key === "vocals",
          },
        ])
      );
      setActiveStemKey("vocals");
      message = "Preset Voz sola aplicado.";
    }

    if (preset === "mix_current") {
      message = "Mix actual listo para exportar.";
    }

    if (preset === "guitar_practice") {
      const target = availableStemKeys.includes("guitar") ? "guitar" : "other";
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: key === target,
            solo: false,
          },
        ])
      );
      setActiveStemKey(target);
      message =
        target === "guitar"
          ? "Preset Guitar practice aplicado."
          : "Preset Guitar practice aplicado (usando canal other).";
    }

    if (preset === "drum_focus") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...base[key],
            muted: false,
            solo: key === "drums",
          },
        ])
      );
      setActiveStemKey("drums");
      message = "Preset Drum focus aplicado.";
    }

    if (preset === "full_mix") {
      next = Object.fromEntries(
        availableStemKeys.map((key) => [
          key,
          {
            ...DEFAULT_STEM_SETTINGS,
            volume: 1,
          },
        ])
      );
      setActiveStemKey("vocals");
      message = "Preset Full mix aplicado.";
    }

    setStemSettings(next);
    trackEvent("quick_preset_used", { preset });
    toast.success(message);
  };

  const buildExportStemPayload = () => {
    const requiredSettings = ensureStemSettings(BASE_STEMS, stemSettings);
    const hasSolo = BASE_STEMS.some((key) => Boolean(requiredSettings[key]?.solo));

    return Object.fromEntries(
      BASE_STEMS.map((key) => {
        const settings = requiredSettings[key] || DEFAULT_STEM_SETTINGS;
        const enabled =
          !settings.muted &&
          Number(settings.volume || 0) > 0 &&
          (!hasSolo || Boolean(settings.solo));
        return [
          key,
          {
            enabled,
            volume: enabled ? clamp(Number(settings.volume || 0), 0, 1) : 0,
          },
        ];
      })
    );
  };

  const resolveExportMode = () => {
    const requiredSettings = ensureStemSettings(BASE_STEMS, stemSettings);
    const hasSolo = BASE_STEMS.some((key) => Boolean(requiredSettings[key]?.solo));
    return hasSolo ? "sum_selected_stems" : "subtract_from_master";
  };

  const getExportErrorMessage = async (error) => {
    let responseData = error?.response?.data;
    if (responseData instanceof Blob) {
      try {
        const rawText = await responseData.text();
        responseData = rawText ? JSON.parse(rawText) : null;
      } catch (_parseError) {
        responseData = null;
      }
    }

    const detail = responseData?.detail;
    const code = detail?.code || detail;
    const fallback = detail?.message || "No fue posible exportar la mezcla.";
    const messages = {
      stems_missing: "Faltan stems completos para esta canción. Prepara los stems antes de descargar.",
      stems_pending: "Los stems todavía están en cola o procesándose. Espera a que estén listos.",
      invalid_format: "Formato no soportado. Usa MP3 o WAV.",
      invalid_mode: "Modo de exportaciÃ³n no soportado.",
      unauthorized: "No tienes acceso a esta canción.",
      render_failed: "No se pudo renderizar la mezcla. Revisa que los audios sigan disponibles.",
      stem_longer_than_master:
        "Un stem tiene audio real después del final del master; la descarga fue bloqueada para evitar desincronización.",
      unsafe_source_url: "La fuente de audio no es válida para exportar.",
      unsafe_path: "La ruta temporal de exportación no es segura.",
    };
    return messages[code] || fallback;
  };

  const exportCurrentMix = async () => {
    if (!stemsReady) {
      toast.error(
        stemsTask?.status === "pending" || stemsTask?.status === "processing"
          ? "Los stems todavía se están preparando."
          : "Prepara los stems antes de descargar una mezcla."
      );
      return;
    }

    if (mixExportController.current || !exportSettings.ready) return;
    const controller = new AbortController(); mixExportController.current = controller;
    setExportingMix(true);
    const mode = resolveExportMode();
    const modeLabel =
      mode === "subtract_from_master" ? "Quitando del audio original" : "Aislando stems HQ";
    setExportStatusMsg(
      exportQuality === "hq"
        ? `${modeLabel} · Alta calidad`
        : exportQuality === "balanced"
        ? `${modeLabel} · Calidad balanceada`
        : `${modeLabel} · Rápido`
    );
    try {
      const response = await requestAudioExport(
        "/stem-mixer/export-jobs",
        {
          song_id: analysisId,
          format: exportFormat,
          output_quality: exportSettings.quality,
          mode,
          mix_name: presetName || `${slugifyFilename(analysis?.filename)}_mix`,
          stems: buildExportStemPayload(),
          options: {
            normalize: true,
            master_volume: clamp(masterVolume / 100, 0, 1),
            sequence: exportUseSequence ? sequence || [] : [],
          },
          quality: exportQuality,
          subtraction_strength: exportSubtractionStrength,
          enhance_stems: exportEnhanceStems,
          preserve_quality: true,
        },
        getAuthHeaders(), controller.signal,
        percent => setExportStatusMsg(`${modeLabel} · ${Math.round(percent)}%`)
      );
      if (controller.signal.aborted) return;
      nativeAudioDownload(response.assets[0]);
      toast.success("Mezcla descargada.");
      trackEvent("stem_mix_exported", {
        format: exportFormat,
        quality: exportQuality,
        mode: resolveExportMode(),
        sequence: exportUseSequence,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const message = await getExportErrorMessage(error);
      toast.error(message);
      trackEvent("stem_mix_export_failed", {
        status: error?.response?.status,
        code: error?.response?.data?.detail?.code,
      });
    } finally {
      mixExportController.current = null;
      if (!controller.signal.aborted) {
        setExportingMix(false);
        setExportStatusMsg("");
      }
    }
  };

  const applyPresetFromServer = (preset) => {
    if (!preset) return;
    const keys = Array.from(
      new Set([...availableStemKeys, ...Object.keys(preset.stem_settings || {})])
    );
    setStemSettings(ensureStemSettings(keys, preset.stem_settings || {}));
    setSequence(preset.sequence || []);
    setClickEnabled(Boolean(preset.click_enabled));
    setClickSubdivision(Number(preset.click_subdivision || 1));
    setTransposeSteps(Number(preset.transpose_steps || 0));
    setPresetName(preset.name || "");
    setSelectedPresetId(preset.id);
    sequenceCursorRef.current = 0;
    sequenceRepeatRef.current = 1;
    setSequenceCursor(0);
    setSequenceRepeatProgress(1);
    toast.success(`Preset "${preset.name}" cargado.`);
    trackEvent("preset_loaded", { preset_id: preset.id });
  };

  const buildPresetPayload = () => {
    const requiredStemSettings = ensureStemSettings(
      Array.from(new Set([...BASE_STEMS, ...availableStemKeys])),
      stemSettings
    );

    return {
      analysis_id: analysisId,
      name: (presetName || "Practice session").trim(),
      stem_settings: requiredStemSettings,
      sequence: (sequence || []).map((item) => ({
        section_name: item.section_name,
        start: Number(item.start || 0),
        end: Number(item.end || 0),
        repeats: clamp(Number(item.repeats || 1), 1, 16),
      })),
      bpm: Number(analysis?.bpm || 120),
      click_enabled: clickEnabled,
      click_subdivision: clamp(Number(clickSubdivision || 1), 1, 8),
      transpose_steps: clamp(Number(transposeSteps || 0), -12, 12),
    };
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error("Define un nombre de preset.");
      return;
    }
    if (!isPro) {
      trackEvent("save_preset_free_attempt");
      showProCTA("guarda tus sesiones de ensayo personalizadas");
      return;
    }

    setSavingPreset(true);
    try {
      const response = await axios.post(
        `${API_URL}/stem-mixer/presets`,
        buildPresetPayload(),
        { headers: getAuthHeaders() }
      );
      await fetchPresets();
      setSelectedPresetId(response.data.id);
      toast.success("Preset guardado.");
      trackEvent("preset_saved", { preset_id: response.data.id });
    } catch (error) {
      if (error?.response?.status === 403) {
        trackEvent("preset_save_403");
        showProCTA("guarda tus sesiones de ensayo personalizadas");
      } else {
        toast.error(error?.response?.data?.detail || "No fue posible guardar preset.");
      }
    } finally {
      setSavingPreset(false);
    }
  };

  const updatePreset = async () => {
    if (!selectedPresetId) {
      toast.error("Selecciona un preset para actualizar.");
      return;
    }
    if (!isPro) {
      trackEvent("update_preset_free_attempt", { preset_id: selectedPresetId });
      showProCTA("actualiza presets de ensayo y guarda versiones");
      return;
    }

    setUpdatingPreset(true);
    try {
      const { analysis_id: _ignoredAnalysisId, ...updatePayload } = buildPresetPayload();
      await axios.put(
        `${API_URL}/stem-mixer/presets/${selectedPresetId}`,
        updatePayload,
        { headers: getAuthHeaders() }
      );
      await fetchPresets();
      toast.success("Preset actualizado.");
      trackEvent("preset_updated", { preset_id: selectedPresetId });
    } catch (error) {
      if (error?.response?.status === 403) {
        trackEvent("preset_update_403", { preset_id: selectedPresetId });
        showProCTA("edita presets guardados para tus ensayos");
      } else {
        toast.error(error?.response?.data?.detail || "No fue posible actualizar preset.");
      }
    } finally {
      setUpdatingPreset(false);
    }
  };

  const deletePreset = async () => {
    if (!selectedPresetId) {
      toast.error("Selecciona un preset para eliminar.");
      return;
    }
    if (!isPro) {
      trackEvent("delete_preset_free_attempt", { preset_id: selectedPresetId });
      showProCTA("administra tu librería de presets de ensayo");
      return;
    }

    setDeletingPreset(true);
    try {
      await axios.delete(`${API_URL}/stem-mixer/presets/${selectedPresetId}`, {
        headers: getAuthHeaders(),
      });
      setSelectedPresetId("");
      await fetchPresets();
      toast.success("Preset eliminado.");
      trackEvent("preset_deleted", { preset_id: selectedPresetId });
    } catch (error) {
      if (error?.response?.status === 403) {
        trackEvent("preset_delete_403", { preset_id: selectedPresetId });
        showProCTA("administra tu librería de presets de ensayo");
      } else {
        toast.error(error?.response?.data?.detail || "No fue posible eliminar preset.");
      }
    } finally {
      setDeletingPreset(false);
    }
  };

  const exportSessionJson = () => {
    const exportPayload = {
      song_name: analysis?.filename || "Song",
      analysis_id: analysisId,
      exported_at: new Date().toISOString(),
      bpm: analysis?.bpm || 0,
      key: analysis?.key || null,
      structure: analysis?.structure || [],
      sequence: sequence || [],
      stem_settings: stemSettings,
      click_enabled: clickEnabled,
      click_subdivision: clickSubdivision,
      transpose_steps: transposeSteps,
      stems_ready: stemsReady,
      stems: stemsTask?.stems || {},
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${slugifyFilename(analysis?.filename)}_preset.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    toast.success("Sesión exportada en JSON.");
    trackEvent("preset_json_exported");
  };

  const addSectionToSequence = (sectionIndex) => {
    const section = analysis?.structure?.[sectionIndex];
    if (!section) return;
    setSequence((prev) => [
      ...prev,
      {
        section_name: section.name,
        start: Number(section.start || 0),
        end: Number(section.end || 0),
        repeats: 1,
      },
    ]);
  };

  const moveSequenceItem = (index, direction) => {
    setSequence((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const updateSequenceRepeats = (index, repeats) => {
    setSequence((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, repeats: clamp(Number(repeats || 1), 1, 16) }
          : item
      )
    );
  };

  const removeSequenceItem = (index) => {
    setSequence((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const startSequenceMode = () => {
    if (!sequence.length) {
      toast.error("Agrega secciones a la secuencia.");
      return;
    }
    sequenceCursorRef.current = 0;
    sequenceRepeatRef.current = 1;
    setSequenceCursor(0);
    setSequenceRepeatProgress(1);
    setSequenceModeEnabled(true);
    seekTo(sequence[0].start);
    trackEvent("sequence_mode_started");
  };

  const stopSequenceMode = () => {
    setSequenceModeEnabled(false);
    setSequenceCursor(0);
    setSequenceRepeatProgress(1);
    sequenceCursorRef.current = 0;
    sequenceRepeatRef.current = 1;
    trackEvent("sequence_mode_stopped");
  };

  const progressPercent = duration > 0 ? clamp((currentTime / duration) * 100, 0, 100) : 0;
  const stemsProcessing = ["pending", "processing"].includes(stemsTask?.status);
  const stemsFailed = stemsTask?.status === "failed" || ["failed", "stale"].includes(stemsTask?.processing_status);
  const stemProgressLabel = stemStatusLabel(stemsTask);
  const stemChordLoading = stemChordLoadingKey === stemChordCacheKey;
  const chordSourceLabel = usingStemChordTimeline
    ? `${STEM_META[activeStemKey]?.label || activeStemKey} / ${stemAnalysisInstrument}`
    : "Global";
  const activeChord = chordSync.activeChord;
  const displayedChord = activeChord
    ? transposeChord(activeChord.chord, isPro ? transposeSteps : 0)
    : "No chord";
  const displayedNextChord = chordSync.nextChord
    ? transposeChord(chordSync.nextChord.chord, isPro ? transposeSteps : 0)
    : null;
  const timeToNextChord = chordSync.timeToNextChord;
  const pianoActiveNotes = useMemo(() => {
    const detectedNotes = activeChord?.detectedNotes || activeChord?.detected_notes;
    if (Array.isArray(detectedNotes) && detectedNotes.length && (!isPro || transposeSteps === 0)) {
      return detectedNotes;
    }
    const chordNotes = getChordNotes(displayedChord);
    if (learningInstrument === "bass") {
      return activeChord?.bass_note || activeChord?.root ? [activeChord.bass_note || activeChord.root] : chordNotes.slice(0, 1);
    }
    return chordNotes;
  }, [activeChord, displayedChord, isPro, learningInstrument, transposeSteps]);
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-gray-400">
        No se encontró el análisis.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-white">
      <audio
        ref={baseAudioRef}
        src={resolveMediaUrl(playbackAudioUrl) || undefined}
        preload="metadata"
        onError={() => {
          console.warn("[StemMixerPage] Playback audio failed", {
            audio_file_ext: analysis.audio_file_ext,
          });
          toast.error("El audio no es reproducible en este navegador. Revisa el preview de audio.");
        }}
      />
      {stemsReady &&
        availableStemKeys.map((key) => (
          <audio
            key={key}
            ref={(element) => {
              stemAudioRefs.current[key] = element;
            }}
            src={resolveMediaUrl(stemsTask?.stems?.[key])}
            preload="none"
          />
        ))}

      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-raised/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 md:px-6">
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <Link to={`/analysis/${analysisId}`} className="grid min-h-11 min-w-11 place-items-center text-gray-400 hover:text-white" aria-label="Volver al análisis">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wider text-gray-400">Stem Mixer Studio</p>
              <h1 className="max-w-full truncate text-lg font-bold md:text-2xl">{analysis.filename}</h1>
              <p className="text-xs md:text-sm text-gray-300">
                Sección actual: <span className="text-primary font-semibold">{currentSectionName}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Badge variant="outline" className={`${isPro ? "border-brass/40 text-brass" : "border-white/20 text-gray-200"} flex items-center gap-1`}>
              <Crown className="w-3 h-3" />
              {isPro ? "Pro" : "Free"}
            </Badge>
            {!isPro && (
              <Button asChild className="min-h-10 flex-1 bg-primary hover:bg-primary/90 sm:flex-none">
                <Link to="/pricing">Upgrade a PRO</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-5 px-3 py-4 sm:px-4 md:px-6 md:py-6">
        <Card className="border-white/10 bg-panel steel-studio-card">
          <CardContent className="space-y-4 p-3 sm:p-4 md:p-6 md:space-y-5">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-3">
              <Badge className="justify-center bg-primary/20 text-primary border border-primary/30">{analysis.key}</Badge>
              <Badge className="justify-center bg-secondary/20 text-secondary border border-secondary/30">{analysis.bpm} BPM</Badge>
              <Badge className="justify-center bg-white/10 border border-white/20 text-white">{analysis.time_signature}</Badge>
              <Badge className="justify-center bg-white/10 border border-white/20 text-white">{formatTime(duration)}</Badge>
              <Badge
                className={`justify-center border ${
                  stemsReady
                    ? "bg-tune/20 text-tune border-tune/30"
                    : "bg-white/10 text-gray-300 border-white/20"
                }`}
              >
                {stemProgressLabel}
              </Badge>
            </div>

            <div className="grid items-center gap-4 lg:grid-cols-[1fr_auto]">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-signal/60">
                    Aprendizaje desde la primera vista
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-black/20 p-1">
                      {["global", "stem"].map((mode) => {
                        const active = chordSourceMode === mode;
                        return (
                          <button
                            key={mode}
                            type="button"
                            disabled={mode === "stem" && !stemsReady}
                            onClick={() => setChordSourceMode(mode)}
                            className={`min-h-8 rounded-lg px-3 text-xs font-semibold transition-colors ${
                              active ? "bg-signal text-slate-950" : "text-gray-300 hover:bg-white/10 hover:text-white"
                            } disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            {mode === "global" ? "Global" : "Stem"}
                          </button>
                        );
                      })}
                    </div>
                    <Badge className="border border-signal/25 bg-signal/10 text-signal">
                      {stemChordLoading ? "Analizando stem" : chordSourceLabel}
                    </Badge>
                  </div>
                </div>
                {chordSourceMode === "stem" && (
                  <div className="flex flex-wrap items-center gap-2 px-1 text-xs text-gray-400">
                    <span>Stem visual: {STEM_META[activeStemKey]?.label || activeStemKey}</span>
                    <span>Instrumento: {stemAnalysisInstrument}</span>
                    {stemChordError ? <span className="text-red-300">{stemChordError}</span> : null}
                    {!stemChordLoading && activeStemChordAnalysis && !stemChords.length ? (
                      <span className="text-brass">Sin acordes confiables en este stem.</span>
                    ) : null}
                  </div>
                )}
                <ChordLearningPanel
                  chord={displayedChord}
                  activeNotes={pianoActiveNotes}
                  nextChord={displayedNextChord}
                  timeToNextChord={timeToNextChord}
                  currentTime={currentTime}
                  chords={chords}
                  compact
                  showLabels
                  instrumentMode={learningInstrument}
                  onInstrumentModeChange={handleLearningInstrumentChange}
                  confidence={activeChord?.confidence}
                  bassRootOnly={learningInstrument === "bass" && activeChord?.inferredChord === false}
                />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-end">
                <Button variant="outline" className="min-h-11 border-white/20 text-gray-200" onClick={() => skipBy(-10)}>
                  <SkipBack className="w-4 h-4 mr-1" /> -10s
                </Button>
                <Button className="h-12 w-12 rounded-full bg-primary hover:bg-primary/90" onClick={togglePlay}>
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                </Button>
                <Button variant="outline" className="min-h-11 border-white/20 text-gray-200" onClick={() => skipBy(10)}>
                  +10s <SkipForward className="w-4 h-4 ml-1" />
                </Button>
                <Button variant="ghost" className="min-h-11 text-gray-300" onClick={restartPlayback}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restart
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div
                ref={progressBarRef}
                className="relative h-5 cursor-pointer overflow-hidden rounded-full bg-white/10 shadow-inner touch-none focus:outline-none focus:ring-2 focus:ring-primary/70"
                role="slider"
                aria-label="Buscar en la canción"
                aria-valuemin={0}
                aria-valuemax={Math.round(duration || 0)}
                aria-valuenow={Math.round(currentTime || 0)}
                tabIndex={0}
                onPointerDown={handleTimelinePointerDown}
                onPointerMove={handleTimelinePointerMove}
                onPointerUp={handleTimelinePointerUp}
                onPointerCancel={handleTimelinePointerCancel}
                onKeyDown={handleTimelineKeyDown}
              >
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-signal" style={{ width: `${progressPercent}%` }} />
                {(analysis.structure || []).map((section, idx) => (
                  <button
                    key={`${section.name}-${idx}`}
                    type="button"
                    className="absolute top-0 bottom-0 w-1 rounded-full bg-white/45 hover:bg-white"
                    style={{ left: `${duration ? (section.start / duration) * 100 : 0}%` }}
                    onClick={() => seekTo(section.start)}
                    aria-label={`Ir a ${section.name}`}
                    title={`${section.name} (${formatTime(section.start)})`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-400 font-mono">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Button
                variant="outline"
                className="min-h-11 border-white/20 bg-black/20 text-white"
                onClick={() => applyQuickPreset("karaoke")}
                disabled={!stemsReady}
              >
                🎤 Karaoke
              </Button>
              <Button
                variant="outline"
                className="min-h-11 border-white/20 bg-black/20 text-white"
                onClick={() => applyQuickPreset("guitar_practice")}
                disabled={!stemsReady}
              >
                🎸 Guitar practice
              </Button>
              <Button
                variant="outline"
                className="min-h-11 border-white/20 bg-black/20 text-white"
                onClick={() => applyQuickPreset("drum_focus")}
                disabled={!stemsReady}
              >
                🥁 Drum focus
              </Button>
              <Button
                variant="outline"
                className="min-h-11 border-white/20 bg-black/20 text-white"
                onClick={() => applyQuickPreset("full_mix")}
                disabled={!stemsReady}
              >
                🎧 Full mix
              </Button>
            </div>
          </CardContent>
        </Card>

        {!isPro && (
          <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-signal/10 steel-studio-card">
            <CardContent className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="font-semibold text-white">🔒 Solo en PRO — guarda tus sesiones de ensayo personalizadas</p>
                <p className="text-sm text-gray-300">
                  Desbloquea velocidad variable, presets persistentes y flujo de práctica avanzado.
                </p>
              </div>
              <Button asChild className="bg-primary hover:bg-primary/90 w-full md:w-auto">
                <Link to="/pricing">Upgrade a PRO</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid xl:grid-cols-[1.45fr_1fr] gap-6">
          <section className="space-y-4">
            <Card className="border-white/10 bg-panel steel-studio-card">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="flex flex-col gap-3 text-base sm:flex-row sm:items-center sm:justify-between">
                  <span className="flex items-center gap-2">
                    <Waves className="w-4 h-4 text-primary" />
                    Mixer por instrumento
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    {stemsFailed && <Button
                      variant="outline"
                      className="min-h-10 border-white/20 text-gray-200"
                      onClick={prepareStems}
                      disabled={preparingStems || stemsProcessing}
                    >
                      {preparingStems || stemsProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {preparingStems ? "Reintentando..." : "Reintentar stems"}
                    </Button>}
                    <Badge variant="outline" className="border-white/20 text-gray-300">
                      Master {masterVolume}%
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {stemsStatusError && <p role="alert" className="text-sm text-red-300">{stemsStatusError}</p>}
                {!stemsReady && (
                  <div className="space-y-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p role="status" className="text-sm text-gray-300">{stemProgressLabel}</p>
                      {stemsProcessing && (
                        <Badge className="w-fit border border-signal/25 bg-signal/10 text-signal">
                          {stemProgressLabel}
                        </Badge>
                      )}
                    </div>
                    {stemsTask?.status && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2 text-xs text-gray-400">
                          <span>Estado: {stemProgressLabel}</span>
                        </div>
                        {stemsProcessing && (
                          <div className="flex flex-col gap-2 rounded-xl bg-black/25 p-2 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-gray-300">
                              Puedes seguir usando SteelMora mientras se procesa.
                            </p>
                            <Button asChild size="sm" variant="outline" className="min-h-9 border-white/20 text-gray-200">
                              <Link to="/dashboard">Seguir usando la app</Link>
                            </Button>
                          </div>
                        )}
                        {stemsFailed && (
                          <p className="text-xs text-red-300">
                            {stemsTask.error || "La separacion fallo. Reintenta cuando el worker este disponible."}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Volumen general</p>
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="text-gray-300" onClick={() => setMasterVolume((prev) => (prev > 0 ? 0 : 90))} aria-label="Alternar volumen master">
                      {masterVolume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </Button>
                    <Slider
                      value={[masterVolume]}
                      onValueChange={(value) => setMasterVolume(clamp(Number(value[0] || 0), 0, 100))}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>

                <div className="grid gap-2.5 sm:grid-cols-2 sm:gap-3">
                  {availableStemKeys.map((key) => {
                    const settings = stemSettings[key] || DEFAULT_STEM_SETTINGS;
                    const meta = STEM_META[key] || {
                      label: key,
                      Icon: AudioWaveform,
                      color: "text-white",
                    };
                    const isActive = activeStemKey === key;
                    const Icon = meta.Icon;

                    return (
                      <div
                        key={key}
                         className={`rounded-[1.15rem] border p-3 space-y-2.5 transition-colors ${
                          isActive ? "border-primary/50 bg-primary/10" : "border-white/10 bg-black/20"
                        }`}
                        onClick={() => {
                          setActiveStemKey(key);
                          const preferredInstrument = getStemAnalysisInstrument(key, learningInstrument);
                          if (["bass", "guitar", "piano"].includes(preferredInstrument) && preferredInstrument !== learningInstrument) {
                            setLearningInstrument(writeLearningInstrumentPreference(preferredInstrument));
                          }
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Icon className={`w-4 h-4 ${meta.color}`} />
                            <p className="truncate font-semibold">{meta.label}</p>
                            {isActive && (
                              <Badge className="bg-primary/20 text-primary border border-primary/40">Activo</Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="border-white/20 text-gray-300">
                            {Math.round((settings.volume || 0) * 100)}%
                          </Badge>
                        </div>

                        <Slider
                          value={[Math.round((settings.volume || 0) * 100)]}
                          onValueChange={(value) =>
                            updateStemSetting(key, {
                              volume: clamp(Number(value[0] || 0) / 100, 0, 1),
                            })
                          }
                          min={0}
                          max={100}
                          step={1}
                        />

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            className={`min-h-10 border-white/20 ${settings.muted ? "bg-red-500/20 text-red-200 border-red-400/40" : "text-gray-200"}`}
                            onClick={() => toggleStemMute(key)}
                            disabled={!stemsReady}
                          >
                            {settings.muted ? "Muted" : "Mute"}
                          </Button>
                          <Button
                            variant="outline"
                            className={`min-h-10 border-border ${settings.solo ? "bg-tune/20 text-tune border-tune/40" : "text-muted-foreground"}`}
                            onClick={() => toggleStemSolo(key)}
                            disabled={!stemsReady}
                          >
                            {settings.solo ? "Solo ON" : "Solo"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="border-signal/15 bg-panel steel-studio-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="w-4 h-4 text-signal" />
                  Exportar mezcla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="space-y-2 rounded-xl border border-white/10 bg-black/20 p-3">
                    <p className="text-xs uppercase tracking-wide text-signal">Quitar del original</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {[
                        ["karaoke", "Karaoke sin voz"],
                        ["no_bass", "Sin bajo"],
                        ["no_drums", "Sin bateria"],
                        ["no_other", "Sin other"],
                        ["instrumental_full", "Instrumental"],
                      ].map(([preset, label]) => (
                        <Button
                          key={preset}
                          variant="outline"
                          className="min-h-10 border-white/20 bg-black/20 text-gray-100"
                          onClick={() => applyQuickPreset(preset)}
                          disabled={!stemsReady}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 rounded-xl border border-white/10 bg-black/20 p-3">
                    <p className="text-xs uppercase tracking-wide text-tune">Aislar stems</p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {[
                        ["vocals_only", "Voz sola"],
                        ["bass_drums", "Bajo + bateria"],
                        ["mix_current", "Mix actual"],
                      ].map(([preset, label]) => (
                        <Button
                          key={preset}
                          variant="outline"
                          className="min-h-10 border-white/20 bg-black/20 text-gray-100"
                          onClick={() => applyQuickPreset(preset)}
                          disabled={!stemsReady}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-gray-400">Calidad</p>
                    <select
                      className="h-11 w-full rounded-xl border border-white/20 bg-black/40 px-3 text-sm"
                      value={exportQuality}
                      onChange={(e) => setExportQuality(e.target.value)}
                    >
                      <option value="fast">Rápido</option>
                      <option value="balanced">Balanceado</option>
                      <option value="hq">Alta calidad</option>
                    </select>
                  </div>

                  <AudioExportControls settings={exportSettings} disabled={exportingMix} id="stem-export-format" />

                  <label className="flex min-h-11 items-center gap-2 rounded-xl border border-white/10 bg-black/25 px-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={exportUseSequence}
                      onChange={(event) => setExportUseSequence(event.target.checked)}
                      disabled={!sequence.length}
                      className="h-4 w-4 accent-signal"
                    />
                    Usar secuencia
                  </label>

                  <Button
                    className="min-h-11 bg-primary hover:bg-primary/90"
                    onClick={exportCurrentMix}
                    disabled={!stemsReady || exportingMix || !exportSettings.ready}
                  >
                    {exportingMix ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Descargar
                  </Button>
                </div>

                {exportingMix && exportStatusMsg && (
                  <p className="text-xs text-signal animate-pulse">{exportStatusMsg}</p>
                )}

                <StemDownloads task={stemsTask} settings={exportSettings} getAuthHeaders={getAuthHeaders} />

                <div>
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-200 underline underline-offset-2"
                    onClick={() => setShowAdvancedExport((v) => !v)}
                  >
                    {showAdvancedExport ? "Ocultar opciones avanzadas" : "Opciones avanzadas"}
                  </button>

                  {showAdvancedExport && (
                    <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400">
                          Fuerza de cancelación:{" "}
                          <span className="text-signal font-medium">
                            {Math.round(exportSubtractionStrength * 100)}%
                          </span>
                        </p>
                        <input
                          type="range"
                          min={0.8}
                          max={1.2}
                          step={0.05}
                          value={exportSubtractionStrength}
                          onChange={(e) => setExportSubtractionStrength(Number(e.target.value))}
                          className="w-full accent-signal"
                        />
                        <p className="text-xs text-gray-500">
                          80% – 120% · 100% = cancelación estándar
                        </p>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={exportEnhanceStems}
                          onChange={(e) => setExportEnhanceStems(e.target.checked)}
                          className="h-4 w-4 accent-signal"
                        />
                        Mejorar stems antes de exportar
                      </label>

                      <div className="rounded-lg bg-black/30 px-3 py-2">
                        <p className="text-xs text-gray-500 leading-relaxed">
                          <strong className="text-gray-300">Quitar del original:</strong> SteelMora
                          usa el master original como base y resta stems alineados. No reconstruye
                          la canción sumando stems.
                          <br />
                          <strong className="text-gray-300">Aislar stems:</strong> usa stems HQ
                          alineados y mejorados directamente.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {!stemsReady && (
                  <p className="text-xs text-brass">
                    {stemsTask?.status === "pending" || stemsTask?.status === "processing"
                      ? "Los stems siguen en proceso; la descarga se habilita cuando terminen."
                      : "Prepara los stems completos para habilitar la exportacion."}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-panel steel-studio-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Controles de ensayo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <p className="text-sm font-medium">Loop por sección (Free)</p>
                    <select
                      className="h-11 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm"
                      value={loopSectionIndex === null ? "" : String(loopSectionIndex)}
                      onChange={(event) => {
                        const value = event.target.value;
                        setLoopSectionIndex(value === "" ? null : Number(value));
                      }}
                    >
                      <option value="">Sin selección</option>
                      {(analysis.structure || []).map((section, index) => (
                        <option key={`${section.name}-${index}`} value={index}>
                          {section.name} ({formatTime(section.start)} - {formatTime(section.end)})
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      className={`min-h-11 w-full border-white/20 ${loopEnabled ? "text-primary border-primary/50" : "text-gray-200"}`}
                      onClick={() => setLoopEnabled((prev) => !prev)}
                      disabled={loopSectionIndex === null}
                    >
                      <Repeat className="w-4 h-4 mr-2" />
                      {loopEnabled ? "Loop activo" : "Activar loop"}
                    </Button>
                  </div>

                  <div className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <p className="text-sm font-medium">Velocidad (Pro)</p>
                    <Slider
                      value={[isPro ? playbackRate : 1]}
                      onValueChange={(value) => setPlaybackRate(Number(value[0] || 1))}
                      min={0.5}
                      max={1.5}
                      step={0.05}
                      disabled={!isPro}
                    />
                    <p className="text-xs text-gray-400">
                      {isPro ? `${playbackRate.toFixed(2)}x` : "1.00x (Free)"}
                    </p>
                    {!isPro && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        onClick={() => {
                          trackEvent("playback_rate_locked_cta");
                          showProCTA("controla tempo de práctica de 0.5x a 1.5x");
                        }}
                      >
                        <Lock className="w-3 h-3" />
                        🔒 Solo en PRO — velocidad variable para práctica por pasajes
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <p className="text-sm font-medium">Metrónomo / Click</p>
                    <Button
                      variant="outline"
                      className={`min-h-11 w-full border-white/20 ${clickEnabled ? "text-primary border-primary/50" : "text-gray-200"}`}
                      onClick={() => setClickEnabled((prev) => !prev)}
                    >
                      {clickEnabled ? "Click encendido" : "Activar click"}
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Subdivisión</span>
                      <Input
                        type="number"
                        min={1}
                        max={8}
                        value={clickSubdivision}
                        onChange={(event) =>
                          setClickSubdivision(
                            clamp(Number(event.target.value || 1), 1, 8)
                          )
                        }
                        className="h-8 bg-black/40 border-white/20"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <p className="text-sm font-medium">Transposición visual</p>
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                         className="min-h-11 border-white/20 text-gray-200"
                        disabled={!isPro}
                        onClick={() => setTransposeSteps((prev) => clamp(prev - 1, -12, 12))}
                      >
                        -1
                      </Button>
                      <Badge variant="outline" className="border-white/20 text-gray-200">
                        {isPro ? transposeSteps : 0} semitonos
                      </Badge>
                      <Button
                        variant="outline"
                         className="min-h-11 border-white/20 text-gray-200"
                        disabled={!isPro}
                        onClick={() => setTransposeSteps((prev) => clamp(prev + 1, -12, 12))}
                      >
                        +1
                      </Button>
                    </div>
                    {!isPro && <p className="text-xs text-primary">🔒 Solo en PRO — transposición visual para ensayos rápidos.</p>}
                  </div>

                  <div className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                    <p className="text-sm font-medium">Export sesión</p>
                    <Button
                      variant="outline"
                      className="min-h-11 w-full border-white/20 text-gray-200"
                      onClick={exportSessionJson}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export JSON
                    </Button>
                    <p className="text-xs text-gray-400">
                      Incluye BPM, estructura, secuencia y configuración completa de stems.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <aside className="space-y-4">
            <Card className="border-white/10 bg-panel steel-studio-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Secuenciador de ensayo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {(analysis.structure || []).map((section, index) => (
                    <Button
                      key={`${section.name}-${index}`}
                      size="sm"
                      variant="outline"
                      className="min-h-10 border-white/20 text-gray-200"
                      onClick={() => addSectionToSequence(index)}
                    >
                      + {section.name}
                    </Button>
                  ))}
                </div>

                <div className="max-h-[260px] space-y-2 overflow-auto pr-1 steel-scroll-soft">
                  {sequence.length === 0 ? (
                    <p className="text-sm text-gray-400">Agrega secciones para crear una secuencia de ensayo.</p>
                  ) : (
                    sequence.map((item, index) => (
                      <div key={`${item.section_name}-${index}`} className="space-y-2 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">
                            {index + 1}. {item.section_name}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="min-h-10 min-w-10 text-gray-400"
                            onClick={() => removeSequenceItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-gray-400">
                          {formatTime(item.start)} - {formatTime(item.end)}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">Repeats</span>
                          <Input
                            type="number"
                            min={1}
                            max={16}
                            value={item.repeats}
                            onChange={(event) =>
                              updateSequenceRepeats(index, event.target.value)
                            }
                            className="h-10 border-white/20 bg-black/40"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-10 border-white/20 text-gray-200"
                            onClick={() => moveSequenceItem(index, -1)}
                          >
                            Up
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-10 border-white/20 text-gray-200"
                            onClick={() => moveSequenceItem(index, 1)}
                          >
                            Down
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {!sequenceModeEnabled ? (
                    <Button onClick={startSequenceMode} className="col-span-2 min-h-11 bg-primary hover:bg-primary/90">
                      <Play className="w-4 h-4 mr-2" />
                      Iniciar secuencia
                    </Button>
                  ) : (
                    <Button onClick={stopSequenceMode} variant="outline" className="col-span-2 min-h-11 border-white/20">
                      <Pause className="w-4 h-4 mr-2" />
                      Detener secuencia
                    </Button>
                  )}
                </div>

                {sequenceModeEnabled && sequence.length > 0 && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/10 p-3 text-sm">
                    <p className="font-medium text-primary">
                      Reproduciendo: {sequence[sequenceCursor]?.section_name || "--"}
                    </p>
                    <p className="text-xs text-gray-300">
                      Repetición {sequenceRepeatProgress} de {sequence[sequenceCursor]?.repeats || 1}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-panel steel-studio-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  Presets persistentes
                  {isPro ? (
                    <Badge className="bg-tune/20 text-tune border border-tune/30">Pro</Badge>
                  ) : (
                    <Badge className="bg-brass/20 text-brass border border-brass/30">Free</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={presetName}
                  onChange={(event) => setPresetName(event.target.value)}
                  placeholder="Nombre del preset"
                   className="min-h-11 border-white/20 bg-black/30"
                />

                <select
                   className="h-11 w-full rounded-xl border border-white/20 bg-black/40 px-3 text-sm"
                  value={selectedPresetId}
                  onChange={(event) => setSelectedPresetId(event.target.value)}
                >
                  <option value="">Selecciona un preset</option>
                  {presets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name}
                    </option>
                  ))}
                </select>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="min-h-11 border-white/20 text-gray-200"
                    disabled={!selectedPresetId}
                    onClick={() => {
                      const preset = presets.find((item) => item.id === selectedPresetId);
                      applyPresetFromServer(preset);
                    }}
                  >
                    Cargar
                  </Button>
                  <Button
                    variant="outline"
                    className="min-h-11 border-white/20 text-gray-200"
                    onClick={fetchPresets}
                    disabled={presetsLoading}
                  >
                    {presetsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refrescar"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <Button
                    className="min-h-11 bg-primary hover:bg-primary/90"
                    onClick={savePreset}
                    disabled={savingPreset}
                  >
                    {savingPreset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar preset
                  </Button>
                  <Button
                    variant="outline"
                     className="min-h-11 border-white/20 text-gray-200"
                    onClick={updatePreset}
                    disabled={updatingPreset || !selectedPresetId}
                  >
                    {updatingPreset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Actualizar preset
                  </Button>
                  <Button
                    variant="outline"
                    className="min-h-11 border-red-500/40 text-red-200"
                    onClick={deletePreset}
                    disabled={deletingPreset || !selectedPresetId}
                  >
                    {deletingPreset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Eliminar preset
                  </Button>
                </div>

                {!isPro && (
                  <div className="rounded-2xl border border-brass/30 bg-brass/10 p-3 text-sm">
                    <p className="font-medium text-brass">
                      🔒 Solo en PRO — guarda tus sesiones de ensayo personalizadas
                    </p>
                    <p className="text-xs text-brass/80">
                      En Free puedes mezclar en vivo y exportar JSON local, pero no persistir presets en nube.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-signal/15 bg-panel steel-studio-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-signal" />
                  Soporte y Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-300">
                <p>
                  ¿Algo se desincronizó, tienes una pregunta o quieres reportar una mejora? Tu feedback ayuda a pulir el estudio de ensayo.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {["Preguntas", "Reportes", "Quejas", "Feedback"].map((label) => (
                    <span
                      key={label}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-center text-gray-200"
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <div className="rounded-2xl border border-signal/15 bg-signal/5 p-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-signal/75">SteelMora Lab Studio</p>
                  <p className="mt-1 text-xs text-gray-400">Desarrollado por el Ing. Uriel Velázquez Gamboa</p>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default StemMixerPage;
